import { useRef, useEffect, useState } from "react";
import { createBreak, createSealedItem, deleteSealedItem, fetchBreaks, fetchSealed, ingestBreakCSV } from "../api";
import type { AppConfig, Break, SealedItem } from "../types";

function fmt(v: string | null | undefined) {
  if (!v) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function pnl(cost: string, market: string | null) {
  if (!market) return null;
  const c = parseFloat(cost);
  const m = parseFloat(market);
  if (isNaN(c) || isNaN(m)) return null;
  return m - c;
}

const STATUS_OPTS = ["", "in_stock", "listed", "sold", "broken"];
const GAME_OPTS = ["", "mtg", "pokemon", "yugioh", "lorcana", "onepiece", "other"];

export default function SealedView({ cfg }: { cfg: AppConfig }) {
  const [items, setItems] = useState<SealedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState("in_stock");
  const [game, setGame] = useState("");
  const [selected, setSelected] = useState<SealedItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [breakTarget, setBreakTarget] = useState<SealedItem | null>(null);

  function load() {
    setLoading(true);
    fetchSealed(cfg, { status: status || undefined, game: game || undefined })
      .then(setItems)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, [cfg, status, game]);

  async function handleDelete(item: SealedItem) {
    if (!confirm(`Delete "${item.product_name}"?`)) return;
    try {
      await deleteSealedItem(cfg, item.id);
      setSelected(null);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  const totalCost = items.reduce((s, i) => s + parseFloat(i.cost_per_unit) * i.qty, 0);
  const totalMarket = items.reduce((s, i) => {
    const m = i.market_price ? parseFloat(i.market_price) : parseFloat(i.cost_per_unit);
    return s + m * i.qty;
  }, 0);

  return (
    <>
      <div className="view-header">
        <span className="view-title">Sealed</span>
        <span className="view-count">{items.length}</span>
        <span className="view-total">
          Cost <strong>{totalCost.toLocaleString("en-US", { style: "currency", currency: "USD" })}</strong>
          {" · "}
          Market <strong style={{ color: totalMarket >= totalCost ? "var(--good)" : "var(--bad)" }}>
            {totalMarket.toLocaleString("en-US", { style: "currency", currency: "USD" })}
          </strong>
        </span>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Add</button>
      </div>

      <div className="filter-bar">
        <div className="status-tabs">
          {STATUS_OPTS.map((s) => (
            <button key={s || "all"} className={`status-tab ${status === s ? "active" : ""}`} onClick={() => setStatus(s)}>
              {s || "All"}
            </button>
          ))}
        </div>
        <select className="filter-select" value={game} onChange={(e) => setGame(e.target.value)}>
          {GAME_OPTS.map((g) => <option key={g} value={g}>{g || "All games"}</option>)}
        </select>
      </div>

      <div className="table-wrap">
        {loading && <div className="state-container"><div className="spinner" /></div>}
        {err && <div className="state-container"><p style={{ color: "var(--bad)" }}>{err}</p></div>}
        {!loading && !err && (
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Set</th>
                <th>Game</th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Cost/Unit</th>
                <th style={{ textAlign: "right" }}>Market</th>
                <th style={{ textAlign: "right" }}>Unrealized</th>
                <th>Status</th>
                <th>Acquired</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const p = pnl(item.cost_per_unit, item.market_price);
                return (
                  <tr key={item.id} onClick={() => setSelected(item)} className={selected?.id === item.id ? "selected" : ""}>
                    <td style={{ fontWeight: 500 }}>{item.product_name}</td>
                    <td style={{ color: "var(--text-dim)", fontSize: 12 }}>{item.set_code ?? "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text-dim)" }}>{item.game}</td>
                    <td className="td-money">{item.qty}</td>
                    <td className="td-money">{fmt(item.cost_per_unit)}</td>
                    <td className="td-money">{fmt(item.market_price)}</td>
                    <td className="td-money" style={{ color: p == null ? "var(--text-dim)" : p >= 0 ? "var(--good)" : "var(--bad)" }}>
                      {p == null ? "—" : (p * item.qty).toLocaleString("en-US", { style: "currency", currency: "USD" })}
                    </td>
                    <td><span style={{ fontSize: 11 }}>{item.status}</span></td>
                    <td style={{ fontSize: 12, color: "var(--text-dim)" }}>{item.acquired_at.slice(0, 10)}</td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--text-dim)", padding: 32 }}>No sealed items</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelected(null)} />
          <div className="drawer">
            <div className="drawer-header">
              <h2 style={{ fontSize: 13 }}>{selected.product_name}</h2>
              <button className="drawer-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="drawer-body">
              <div className="drawer-section">
                <span className="drawer-section-title">Details</span>
                {[
                  ["ID", <span className="field-mono">{selected.id}</span>],
                  ["TCGPlayer ID", selected.tcgplayer_product_id ?? "—"],
                  ["Game", selected.game],
                  ["Set Code", selected.set_code ?? "—"],
                  ["Qty", selected.qty],
                  ["Cost/Unit", fmt(selected.cost_per_unit)],
                  ["Market Price", fmt(selected.market_price)],
                  ["Acquired", selected.acquired_at.slice(0, 10)],
                  ["Status", selected.status],
                  ["PO ID", selected.po_id ? <span className="field-mono">{selected.po_id}</span> : "—"],
                  ["Notes", selected.notes ?? "—"],
                ].map(([label, value]) => (
                  <div key={String(label)} className="field-row">
                    <span className="field-label">{label}</span>
                    <span className="field-value">{value}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(selected.status === "in_stock" || selected.status === "listed") && (
                  <button className="btn" onClick={() => { setBreakTarget(selected); setSelected(null); }}>
                    Break
                  </button>
                )}
                <button
                  className="btn"
                  style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                  onClick={() => handleDelete(selected)}
                >
                  Delete
                </button>
              </div>
              {selected && <BreakHistory cfg={cfg} sealedId={selected.id} />}
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <CreateSealedModal
          cfg={cfg}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {breakTarget && (
        <BreakModal
          cfg={cfg}
          item={breakTarget}
          onClose={() => setBreakTarget(null)}
          onCreated={() => { setBreakTarget(null); load(); }}
        />
      )}
    </>
  );
}

function BreakHistory({ cfg, sealedId }: { cfg: AppConfig; sealedId: string }) {
  const [breaks, setBreaks] = useState<Break[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBreaks(cfg, sealedId)
      .then(setBreaks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cfg, sealedId]);

  if (loading) return <div className="spinner" style={{ margin: "8px 0" }} />;
  if (breaks.length === 0) return null;

  return (
    <div className="drawer-section">
      <span className="drawer-section-title">Break History</span>
      {breaks.map((b) => (
        <div key={b.id} style={{ background: "var(--bg-elev-2)", borderRadius: 5, padding: "8px 12px", fontSize: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Broke {b.qty_broken}× on {b.broken_at.slice(0, 10)}</span>
            <span style={{ color: "var(--text-dim)" }}>{b.singles_count} singles</span>
          </div>
          {b.notes && <div style={{ color: "var(--text-dim)", marginTop: 4 }}>{b.notes}</div>}
        </div>
      ))}
    </div>
  );
}

function CreateSealedModal({ cfg, onClose, onCreated }: { cfg: AppConfig; onClose: () => void; onCreated: () => void }) {
  const [productName, setProductName] = useState("");
  const [game, setGame] = useState("mtg");
  const [setCode, setSetCode] = useState("");
  const [qty, setQty] = useState("1");
  const [costPerUnit, setCostPerUnit] = useState("");
  const [acquiredAt, setAcquiredAt] = useState(new Date().toISOString().slice(0, 10));
  const [poId, setPoId] = useState("");
  const [tcgId, setTcgId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await createSealedItem(cfg, {
        product_name: productName,
        game,
        set_code: setCode || null,
        qty: parseInt(qty, 10),
        cost_per_unit: costPerUnit,
        acquired_at: acquiredAt,
        po_id: poId || null,
        tcgplayer_product_id: tcgId ? parseInt(tcgId, 10) : null,
        notes: notes || null,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <h2>Add Sealed Item</h2>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="drawer-body">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input className="form-input" value={productName} onChange={(e) => setProductName(e.target.value)} required />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Game *</label>
                <select className="form-select" value={game} onChange={(e) => setGame(e.target.value)}>
                  {["mtg", "pokemon", "yugioh", "lorcana", "onepiece", "other"].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Set Code</label>
                <input className="form-input" value={setCode} onChange={(e) => setSetCode(e.target.value)} placeholder="e.g. MH3" />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Qty *</label>
                <input className="form-input" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Cost/Unit *</label>
                <input className="form-input" value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} placeholder="0.00" required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Acquired Date *</label>
              <input className="form-input" type="date" value={acquiredAt} onChange={(e) => setAcquiredAt(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">PO ID</label>
              <input className="form-input" value={poId} onChange={(e) => setPoId(e.target.value)} placeholder="UUID of purchase order" />
            </div>
            <div className="form-group">
              <label className="form-label">TCGPlayer Product ID</label>
              <input className="form-input" value={tcgId} onChange={(e) => setTcgId(e.target.value)} placeholder="Numeric ID" />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Add Item"}</button>
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

function BreakModal({ cfg, item, onClose, onCreated }: { cfg: AppConfig; item: SealedItem; onClose: () => void; onCreated: () => void }) {
  const [qty, setQty] = useState("1");
  const [brokenAt, setBrokenAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [createdBreakId, setCreatedBreakId] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const b = await createBreak(cfg, {
        sealed_item_id: item.id,
        qty_broken: parseInt(qty, 10),
        broken_at: brokenAt,
        notes: notes || null,
      });
      setCreatedBreakId(b.id);
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  if (createdBreakId) {
    return (
      <CSVIngestStep
        cfg={cfg}
        breakId={createdBreakId}
        productName={item.product_name}
        onDone={onCreated}
        onSkip={onCreated}
      />
    );
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" style={{ width: 360 }}>
        <div className="drawer-header">
          <h2>Break: {item.product_name}</h2>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="drawer-body">
          <p style={{ fontSize: 13, color: "var(--text-dim)" }}>
            {item.qty} units in stock. Record how many you're breaking.
          </p>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Qty to Break *</label>
              <input className="form-input" type="number" min="1" max={item.qty} value={qty} onChange={(e) => setQty(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Broken Date *</label>
              <input className="form-input" type="date" value={brokenAt} onChange={(e) => setBrokenAt(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Record Break"}</button>
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

function CSVIngestStep({ cfg, breakId, productName, onDone, onSkip }: {
  cfg: AppConfig;
  breakId: string;
  productName: string;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ singles_created: number; cost_per_single: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setErr(null);
    try {
      const r = await ingestBreakCSV(cfg, breakId, file);
      setResult({ singles_created: r.singles_created, cost_per_single: r.cost_per_single });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onSkip} />
      <div className="drawer" style={{ width: 380 }}>
        <div className="drawer-header">
          <h2>Import Singles: {productName}</h2>
          <button className="drawer-close" onClick={onSkip}>✕</button>
        </div>
        <div className="drawer-body">
          {result ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "rgba(52,211,153,0.1)", border: "1px solid var(--good)", borderRadius: 6, padding: "14px 16px" }}>
                <p style={{ color: "var(--good)", fontWeight: 600, marginBottom: 6 }}>
                  ✓ {result.singles_created} singles created
                </p>
                <p style={{ fontSize: 12, color: "var(--text-dim)" }}>
                  Cost basis per single: ${parseFloat(result.cost_per_single).toFixed(4)}
                </p>
              </div>
              <button className="btn btn-primary" onClick={onDone}>Done</button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ fontSize: 13, color: "var(--text-dim)" }}>
                Break recorded. Optionally upload the box-break-app inventory CSV to bulk-create singles with apportioned cost basis.
              </p>
              <div
                className="scan-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f) void handleFile(f);
                }}
              >
                {uploading ? "Uploading…" : "Drop inventory CSV or click to browse"}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }}
              />
              {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={onSkip}>Skip — add singles manually</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
